<template>
  <v-container pa-0>
    <v-row class="no-gutters">
      <v-col cols="12" sm class="background-color-col">
        <span class="label-centered primary white-text">Año</span>
        <v-text-field
          placeholder="2023"
          class="rounded-lg mx-1 my-2"
          v-model="cifrado.anio"
          dense
          maxlength="4"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('anio', $event, 'CI')"
          @keydown="onKeyDown($event, null, 'CI')"
          @keypress="onlyNumbers"
          ref="anio"
          outlined
          :error-messages="anioErrors"
          @blur="$v.cifrado.anio.$touch()"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm class="background-color-col">
        <span class="label-centered primary white-text">CI</span>
        <v-text-field
          class="rounded-lg mx-1 my-2"
          placeholder="0002"
          v-model="cifrado.CI"
          dense
          maxlength="4"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('CI', $event, 'AG')"
          @keydown="onKeyDown($event, 'anio', 'AG')"
          @keypress="onlyNumbers"
          ref="CI"
          outlined
          :error-messages="ciErrors"
          @blur="$v.cifrado.CI.$touch()"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm class="background-color-col">
        <span class="label-centered primary white-text">AG</span>
        <v-text-field
          class="rounded-lg mx-1 my-2"
          placeholder="2"
          v-model="cifrado.AG"
          dense
          maxlength="2"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('AG', $event, 'UP')"
          @keydown="onKeyDown($event, 'CI', 'UP')"
          @keypress="onlyNumbers"
          ref="AG"
          outlined
          :error-messages="agErrors"
          @blur="$v.cifrado.AG.$touch()"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm class="background-color-col">
        <span class="label-centered primary white-text">UP</span>
        <v-text-field
          class="rounded-lg mx-1 my-2"
          placeholder="01"
          v-model="cifrado.UP"
          dense
          maxlength="2"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('UP', $event, 'LT')"
          @keydown="onKeyDown($event, 'AG', 'LT')"
          @keypress="onlyNumbers"
          ref="UP"
          outlined
          :error-messages="upErrors"
          @blur="$v.cifrado.UP.$touch()"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm class="background-color-col">
        <span class="label-centered primary white-text">LT</span>
        <v-text-field
          class="rounded-lg mx-1 my-2"
          placeholder="25"
          v-model="cifrado.LT"
          dense
          maxlength="2"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('LT', $event, 'CE')"
          @keydown="onKeyDown($event, 'UP', 'CE')"
          @keypress="onlyNumbers"
          ref="LT"
          outlined
          :error-messages="ltErrors"
          @blur="$v.cifrado.LT.$touch()"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm class="background-color-col">
        <span class="label-centered primary white-text">CE</span>
        <v-text-field
          class="rounded-lg mx-1 my-2"
          v-model="cifrado.CE"
          dense
          maxlength="2"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('CE', $event, 'FF')"
          @keydown="onKeyDown($event, 'LT', 'FF')"
          @keypress="onlyNumbers"
          ref="CE"
          outlined
          :error-messages="ceErrors"
          @blur="$v.cifrado.CE.$touch()"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm class="background-color-col">
        <span class="label-centered primary white-text">FF</span>
        <v-text-field
          class="rounded-lg mx-1 my-2"
          v-model="cifrado.FF"
          dense
          maxlength="2"
          :readonly="readonly"
          :disabled="readonly"
          @input="onInput('FF', $event, null)"
          @keydown="onKeyDown($event, 'CE', null)"
          @keypress="onlyNumbers"
          ref="FF"
          outlined
          :error-messages="ffErrors"
          @blur="$v.cifrado.FF.$touch()"
          hide-details
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  minLength,
  maxLength,
  numeric,
  required
} from "vuelidate/lib/validators";

export default {
  name: "CifradoPresupuestario",
  props: {
    // Valor inicial o valor controlado desde el padre (string concatenado)
    modelValue: {
      type: String,
      default: ""
    },
    // Modo readonly
    readonly: {
      type: Boolean,
      default: false
    },
    // Valores individuales (alternativa al modelValue)
    value: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      cifrado: {
        anio: "",
        CI: "",
        AG: "",
        UP: "",
        LT: "",
        CE: "",
        FF: ""
      }
    };
  },
  validations() {
    return {
      cifrado: {
        anio: {
          required,
          numeric,
          minLength: minLength(4),
          maxLength: maxLength(4)
        },
        CI: {
          required,
          numeric,
          minLength: minLength(4),
          maxLength: maxLength(4)
        },
        AG: {
          required,
          numeric,
          minLength: minLength(1),
          maxLength: maxLength(1)
        },
        UP: {
          required,
          numeric,
          minLength: minLength(2),
          maxLength: maxLength(2)
        },
        LT: {
          required,
          numeric,
          minLength: minLength(2),
          maxLength: maxLength(2)
        },
        CE: {
          required,
          numeric,
          minLength: minLength(2),
          maxLength: maxLength(2)
        },
        FF: {
          required,
          numeric,
          minLength: minLength(2),
          maxLength: maxLength(2)
        }
      }
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.setCifrado(newValue);
        }
      }
    },
    // Observar cambios en value (objeto) desde el padre
    value: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.cifrado = { ...this.cifrado, ...newValue };
        }
      }
    }
  },
  beforeUnmount() {
    // Solo limpiar si no es readonly
    if (!this.readonly) {
      this.reset();
    }
  },
  computed: {
    cifradoCompleto() {
      return this.cifrado;
    },
    isValid() {
      // Retorna true si todas las validaciones están OK
      return !this.$v.$invalid;
    },
    anioErrors() {
      const errors = [];
      if (!this.$v.cifrado.anio.$dirty) return errors;
      !this.$v.cifrado.anio.required && errors.push("Campo requerido");
      !this.$v.cifrado.anio.numeric && errors.push("El año debe ser numérico");
      !this.$v.cifrado.anio.minLength &&
        errors.push("El año debe tener al menos 4 dígitos");
      !this.$v.cifrado.anio.maxLength &&
        errors.push("El año no debe exceder 4 dígitos");
      return errors;
    },
    ciErrors() {
      const errors = [];
      if (!this.$v.cifrado.CI.$dirty) return errors;
      !this.$v.cifrado.CI.required && errors.push("Campo requerido");
      !this.$v.cifrado.CI.numeric && errors.push("El CI debe ser numérico");
      !this.$v.cifrado.CI.minLength &&
        errors.push("El CI debe tener al menos 2 dígitos");
      !this.$v.cifrado.CI.maxLength &&
        errors.push("El CI no debe exceder 2 dígitos");
      return errors;
    },
    agErrors() {
      const errors = [];
      if (!this.$v.cifrado.AG.$dirty) return errors;
      !this.$v.cifrado.AG.required && errors.push("Campo requerido");
      !this.$v.cifrado.AG.numeric && errors.push("El AG debe ser numérico");
      !this.$v.cifrado.AG.minLength &&
        errors.push("El AG debe tener al menos 2 dígitos");
      !this.$v.cifrado.AG.maxLength &&
        errors.push("El AG no debe exceder 2 dígitos");
      return errors;
    },
    upErrors() {
      const errors = [];
      if (!this.$v.cifrado.UP.$dirty) return errors;
      !this.$v.cifrado.UP.required && errors.push("Campo requerido");
      !this.$v.cifrado.UP.numeric && errors.push("El UP debe ser numérico");
      !this.$v.cifrado.UP.minLength &&
        errors.push("El UP debe tener al menos 2 dígitos");
      !this.$v.cifrado.UP.maxLength &&
        errors.push("El UP no debe exceder 2 dígitos");
      return errors;
    },
    ltErrors() {
      const errors = [];
      if (!this.$v.cifrado.LT.$dirty) return errors;
      !this.$v.cifrado.LT.required && errors.push("Campo requerido");
      !this.$v.cifrado.LT.numeric && errors.push("El LT debe ser numérico");
      !this.$v.cifrado.LT.minLength &&
        errors.push("El LT debe tener al menos 2 dígitos");
      !this.$v.cifrado.LT.maxLength &&
        errors.push("El LT no debe exceder 2 dígitos");
      return errors;
    },
    ceErrors() {
      const errors = [];
      if (!this.$v.cifrado.CE.$dirty) return errors;
      !this.$v.cifrado.CE.required && errors.push("Campo requerido");
      !this.$v.cifrado.CE.numeric && errors.push("El CE debe ser numérico");
      !this.$v.cifrado.CE.minLength &&
        errors.push("El CE debe tener al menos 3 dígitos");
      !this.$v.cifrado.CE.maxLength &&
        errors.push("El CE no debe exceder 3 dígitos");
      return errors;
    },
    ffErrors() {
      const errors = [];
      if (!this.$v.cifrado.FF.$dirty) return errors;
      !this.$v.cifrado.FF.required && errors.push("Campo requerido");
      !this.$v.cifrado.FF.numeric && errors.push("El FF debe ser numérico");
      !this.$v.cifrado.FF.minLength &&
        errors.push("El FF debe tener al menos 3 dígitos");
      !this.$v.cifrado.FF.maxLength &&
        errors.push("El FF no debe exceder 3 dígitos");
      return errors;
    }
  },
  methods: {
    onlyNumbers(event) {
      // Prevenir la entrada de caracteres que no sean números
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },
    onInput(field, event, nextField) {
      // No permitir edición si es readonly
      if (this.readonly) return;

      this.cifrado[field] = event.replace(/[^0-9]/g, "");

      this.emitChange();

      const maxLengths = {
        anio: 4,
        CI: 4,
        AG: 1,
        UP: 2,
        LT: 2,
        CE: 2,
        FF: 2
      };

      if (this.cifrado[field].length === maxLengths[field] && nextField) {
        this.$nextTick(() => {
          this.$refs[nextField].focus();
        });
      }
    },
    emitChange() {
      this.$emit("change", this.cifrado);
      this.$emit("update:modelValue", this.cifradoCompleto);
      this.$emit("validation", this.isValid);
    },
    onKeyDown(event, prevField, nextField) {
      if (event.key === "ArrowRight" && nextField) {
        event.preventDefault();
        this.$refs[nextField].focus();
      } else if (event.key === "ArrowLeft" && prevField) {
        event.preventDefault();
        this.$refs[prevField].focus();
      } else if (
        event.key === "Backspace" &&
        !event.target.value &&
        prevField
      ) {
        event.preventDefault();
        this.$refs[prevField].focus();
      }
    },
    getCifrado() {
      return this.cifradoCompleto;
    },
    setCifrado(cifrado) {
      if (cifrado && typeof cifrado === "string") {
        // Remover guiones si existen (formato: ####-####-#-##-##-##-##)
        const cifradoLimpio = cifrado.replace(/-/g, "");

        this.cifrado.anio = cifradoLimpio.substring(0, 4) || "";
        this.cifrado.CI = cifradoLimpio.substring(4, 8) || "";
        this.cifrado.AG = cifradoLimpio.substring(8, 9) || "";
        this.cifrado.UP = cifradoLimpio.substring(9, 11) || "";
        this.cifrado.LT = cifradoLimpio.substring(11, 13) || "";
        this.cifrado.CE = cifradoLimpio.substring(13, 15) || "";
        this.cifrado.FF = cifradoLimpio.substring(15, 17) || "";
      } else if (typeof cifrado === "object") {
        this.cifrado = cifrado;
      }
    },
    reset() {
      this.cifrado = {
        anio: "",
        CI: "",
        AG: "",
        UP: "",
        LT: "",
        CE: "",
        FF: ""
      };
    }
  }
};
</script>

<style scoped>
.border-r-col {
  border-right: 1px solid #c0c0c0;
}
.background-color-col {
  background-color: #ededf1;
}

.cifrado-presupuestario {
  max-width: 600px;
}

.label-centered {
  height: 50px;
  display: block;
  text-align: center;
  color: white;
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-row {
  display: flex;
  gap: 8px;
}

.header-cell {
  flex: 1;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  background-color: #2c3e50;
  padding: 8px;
  border-radius: 4px;
}

.fields-row {
  display: flex;
  gap: 8px;
}

.cifrado-field {
  flex: 1;
}

.cifrado-field :deep(.v-field__input) {
  text-align: center;
  font-weight: 500;
  font-size: 13px;
}

.cifrado-field :deep(input) {
  text-align: center;
  font-size: 13px;
}

/* Centrar texto en todos los inputs */
:deep(.v-text-field input) {
  text-align: center !important;
  font-size: 13px;
}

:deep(.v-text-field .v-field__input) {
  text-align: center !important;
}

/* Placeholder más pequeño */
:deep(.v-text-field input::placeholder) {
  font-size: 13px;
  opacity: 0.7;
  text-align: center;
}

:deep(.v-text-field input::-webkit-input-placeholder) {
  font-size: 13px;
  opacity: 0.7;
  text-align: center;
}

:deep(.v-text-field input::-moz-placeholder) {
  font-size: 13px;
  opacity: 0.7;
  text-align: center;
}
</style>